import { styled, SxProps, Theme, useThemeProps } from '@mui/material/styles';
import { HTMLMotionProps, motion } from 'motion/react';
import { createResponsiveStyles, resolveThemeColor } from '../../theme';
import { BaseButtonProps, ButtonHeight } from '../../theme/types';
import * as React from 'react';
import { ButtonName } from '../../theme/componentNames';

interface ButtonOwnerState extends BaseButtonProps {
  disableMaxWidth?: boolean;
  buttonName?: ButtonName;
  increaseSpecificity?: boolean;
  fullWidth?: boolean;
  disableResponsiveStyles?: boolean;
}
export interface InnerButtonProps extends Omit<HTMLMotionProps<'button'>, 'color'> {
  ownerState: ButtonOwnerState;
}

const ButtonBase = styled(motion.button)(() => ({
  fontFamily: 'inherit',
  lineHeight: 'inherit',
}));

const convertHeightToResponsiveStyles = (height: ButtonHeight | undefined) => {
  if (height == null) {
    return;
  }
  const heightValue =
    typeof height === 'number' || typeof height === 'string'
      ? {
          xs: height,
          sm: height,
          md: height,
          lg: height,
          xl: height,
        }
      : height;

  return Object.entries(heightValue).reduce((acc, [key, value]) => {
    acc[key] = {
      height: value,
    };
    return acc;
  }, {} as Record<string, React.CSSProperties>);
};

const SolidButton = styled(ButtonBase, {
  name: 'BCSolidButton',
  slot: 'Root',
})<InnerButtonProps>(({ theme, ownerState }) => {
  const {
    color = 'primary',
    buttonName,
    disableMaxWidth,
    increaseSpecificity,
    height,
    fullWidth,
    disableResponsiveStyles,
  } = ownerState;

  const themeColor = resolveThemeColor(theme, color);

  const cssStyleOverrides = buttonName && theme.componentCSSOverrides?.[buttonName];
  const heightStyles = convertHeightToResponsiveStyles(height);
  const defaultStyles = {
    backgroundColor: themeColor.main,
    color: themeColor.contrastText,
    ...(fullWidth && { width: '100%' }),
    ...(disableResponsiveStyles ? {} : createResponsiveStyles(theme, heightStyles, cssStyleOverrides)),
  };
  const styles = {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
    font: theme.vars.font.button,
    fontWeight: theme.typography.button?.fontWeight,
    ...defaultStyles,
    ':hover': {
      // this is redundant but neccessary to prevent third party client style overrides
      ...defaultStyles,
    },
    transition: 'transform 0.3s',
    '&:active': {
      '&:not([disabled])': {
        transform: 'scale(0.95)',
      },
    },
    'not(:focus-visible)': {
      outline: 'none',
    },
    padding: '8.5px 17px', // padding top and bottom of 7px plus 1.5px for border
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      font: theme.vars.font.smButton,
      fontWeight: theme.typography.smButton?.fontWeight,
      ...(!disableMaxWidth && { maxWidth: 333 }),
    },

    '.left_icon': {
      marginRight: 6,
    },
    '.button_img': {
      marginRight: 6,
      maxWidth: 18,
      maxHeight: 18,
    },
  };

  if (increaseSpecificity) {
    return {
      '&&': {
        ...styles,
      },
    };
  }
  return styles;
});

const OutlineButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => !['disableMaxWidth', 'sx', 'increaseSpecificity'].includes(prop as string),
  name: 'BCOutlineButton',
  slot: 'Root',
})<InnerButtonProps>(({ theme, ownerState }) => {
  const {
    color = 'primary',
    buttonName,
    disableMaxWidth,
    increaseSpecificity,
    height,
    fullWidth,
    disableResponsiveStyles,
  } = ownerState;

  const themeColor = resolveThemeColor(theme, color);

  const cssStyleOverrides = buttonName && theme.componentCSSOverrides?.[buttonName];
  const heightStyles = convertHeightToResponsiveStyles(height);
  const defaultStyles = {
    color: themeColor.main,
    borderColor: themeColor.main,
    backgroundColor: 'transparent',
    ...(fullWidth && { width: '100%' }),
    ...(disableResponsiveStyles ? {} : createResponsiveStyles(theme, heightStyles, cssStyleOverrides)),
  };
  const styles = {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    font: theme.vars.font.button,
    fontWeight: theme.typography.button?.fontWeight,
    ...defaultStyles,
    ':hover': {
      ...defaultStyles,
    },
    transition: 'transform 0.3s',
    '&:active': {
      '&:not([disabled])': {
        transform: 'scale(0.95)',
      },
    },
    'not(:focus-visible)': {
      outline: 'none',
    },
    padding: '7px 17px',

    ':disabled': {
      opacity: 1,
      color: theme.vars.palette.action.disabled,
      cursor: 'not-allowed',
      borderColor: theme.vars.palette.action.disabled,
    },
    [theme.breakpoints.down('sm')]: {
      font: theme.vars.font.smButton,
      fontWeight: theme.typography.smButton?.fontWeight,
      ...(!disableMaxWidth && { maxWidth: 333 }),
    },

    '.button_img': {
      marginRight: 6,
      maxWidth: 18,
      maxHeight: 18,
    },
  } as const;
  if (increaseSpecificity) {
    return {
      '&&': {
        ...styles,
      },
    };
  }
  return styles;
});

const TextButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => !['sx', 'increaseSpecificity'].includes(prop as string),
  name: 'BCTextButton',
  slot: 'Root',
})<InnerButtonProps>(({ theme, ownerState }) => {
  const { color = 'primary', buttonName, increaseSpecificity, fullWidth, disableResponsiveStyles } = ownerState;
  const resolvedColor = color === 'default' ? theme.vars.palette.text.primary : resolveThemeColor(theme, color)?.main;
  const cssStyleOverrides = buttonName && theme.componentCSSOverrides?.[buttonName];
  const defaultStyles = {
    color: resolvedColor,
    backgroundColor: 'transparent',
    ...(fullWidth && { width: '100%' }),
    ...(disableResponsiveStyles ? {} : createResponsiveStyles(theme, cssStyleOverrides)),
  };

  const styles = {
    font: theme.vars.font.button,
    fontWeight: theme.typography.button?.fontWeight,
    ...defaultStyles,
    textTransform: 'unset', // resets any potentially added text transformation; if required, add them in the sx prop where needed
    width: 'auto',
    height: 'auto',
    minWidth: 'fit-content',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: 'unset',
    'not(:focus-visible)': {
      outline: 'none',
    },
    border: 'none',
    padding: 0,
    margin: 0,
    fontSize: 'inherit',
    ':disabled': {
      ...defaultStyles,
      cursor: 'not-allowed',
      opacity: 1,
      color: theme.vars.palette.action.disabled,
    },

    '.left_icon': {
      marginRight: 6,
    },
    '.button_img': {
      marginRight: 6,
      maxWidth: 18,
      maxHeight: 18,
    },
  } as const;
  if (increaseSpecificity) {
    return {
      '&&': {
        ...styles,
      },
    };
  }
  return styles;
});

const IconButton = styled(TextButton, {
  shouldForwardProp: (prop) => !['sx', 'increaseSpecificity'].includes(prop as string),
  name: 'BCIconButton',
  slot: 'Root',
  skipVariantsResolver: true,
})<InnerButtonProps>(() => {
  // const { height, increaseSpecificity } = ownerState;
  // const squareSizeStyles = createResponsiveStyles(theme, convertSquareSizeToResponsiveStyles(height));
  // const styles = {
  //   ...squareSizeStyles,
  // };
  // if (increaseSpecificity) {
  //   return {
  //     '&&': {
  //       ...styles,
  //     },
  //   };
  // }
  // return styles;
  return {};
});

type ButtonVariantType = 'solid' | 'outline' | 'text' | 'icon';
// all button types share the same base component essentially making them all have the same props
export interface ButtonProps extends Omit<InnerButtonProps, 'ownerState'>, ButtonOwnerState {
  variant?: ButtonVariantType;
  sx?: SxProps<Theme>;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ variant = 'solid', ...inProps }, ref) => {
  const ButtonComponentName = {
    solid: 'BCSolidButton',
    outline: 'BCOutlineButton',
    text: 'BCTextButton',
    icon: 'BCIconButton',
  }[variant];
  const props = useThemeProps({
    props: inProps,
    name: ButtonComponentName,
  });
  const {
    disableMaxWidth,
    color,
    buttonName,
    increaseSpecificity,
    height,
    disableResponsiveStyles,
    ...rest
  } = props;
  // The ownerState is a special name that will not spread to the DOM via the styled API
  // https://v6.mui.com/material-ui/customization/creating-themed-components/#3-style-the-slot-with-ownerstate
  const ownerState = {
    ...props,
    variant,
    disableMaxWidth,
    color,
    buttonName,
    increaseSpecificity,
    height,
    disableResponsiveStyles,
  };
  switch (variant) {
    case 'solid': {
      if (props.disabled) {
        return (
          <OutlineButton
            ref={ref}
            type="button"
            ownerState={ownerState}
            data-theme-color={color}
            data-theme-variant="outline-button"
            data-theme-component-name={buttonName}
            {...rest}
          />
        );
      }
      // Submit type allows form submission on enter key press
      return (
        <SolidButton
          ref={ref}
          type="submit"
          ownerState={ownerState}
          data-theme-color={color}
          data-theme-variant="solid-button"
          data-theme-component-name={buttonName}
          {...rest}
        />
      );
    }
    case 'outline':
      return (
        <OutlineButton
          ref={ref}
          type="button"
          ownerState={ownerState}
          data-theme-color={color}
          data-theme-variant="outline-button"
          data-theme-component-name={buttonName}
          {...rest}
        />
      );
    case 'text':
      return (
        <TextButton
          ref={ref}
          type="button"
          ownerState={ownerState}
          data-theme-color={color}
          data-theme-variant="text-button"
          data-theme-component-name={buttonName}
          {...rest}
        />
      );
    case 'icon':
      return (
        <IconButton
          ref={ref}
          type="button"
          ownerState={ownerState}
          data-theme-color={color}
          data-theme-variant="icon-button"
          data-theme-component-name={buttonName}
          {...rest}
        />
      );
    default:
      throw new Error(`Invalid variant: ${variant}. Expected 'solid', 'outline', or 'text'.`);
  }
});
Button.displayName = 'Button';

export default Button;
